import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import '../styles/ReadMoreText.css'

const DURATION_MS = 260

const prefersReducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * Clamps `message` to `lines` lines with an ellipsis and a "show more" link.
 * Expanding and collapsing animate the height instead of swapping the text.
 *
 * How the animation works: the wrapper gets an explicit pixel height for the
 * current state, the content is unclamped, then the wrapper is given the
 * target height and CSS transitions between the two. When it settles, the
 * inline height is cleared (auto) and, for a collapse, the clamp is re-applied.
 */
function ReadMoreText({ message, lines = 3, className = '' }) {
    const { t } = useTranslation()
    const wrapperRef = useRef(null)
    const contentRef = useRef(null)
    const targetHeightRef = useRef(null)
    const timerRef = useRef(null)

    const [expanded, setExpanded] = useState(false)
    // Clamp styles are applied only when fully collapsed; during a collapse
    // animation the text stays unclamped so it can be seen shrinking away.
    const [clamped, setClamped] = useState(true)
    const [animating, setAnimating] = useState(false)
    const [overflows, setOverflows] = useState(false)

    // New text starts collapsed again.
    useEffect(() => {
        setExpanded(false)
        setClamped(true)
        setAnimating(false)
    }, [message, lines])

    useEffect(() => () => clearTimeout(timerRef.current), [])

    // Only show the toggle when the clamp actually hides something. Re-check
    // on resize because a wider column can make the text fit.
    useLayoutEffect(() => {
        const el = contentRef.current
        if (!el || !clamped) return undefined
        const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1)
        check()
        if (typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(check)
        observer.observe(el)
        return () => observer.disconnect()
    }, [clamped, message, lines])

    // Second half of the animation: the state has flipped and the content is
    // unclamped, so the target height can be measured and the transition run.
    useLayoutEffect(() => {
        if (!animating) return undefined
        const wrapper = wrapperRef.current
        const content = contentRef.current
        if (!wrapper || !content) return undefined

        const target = expanded ? content.scrollHeight : targetHeightRef.current
        // Reflow so the transition starts from the inline height set on click.
        void wrapper.offsetHeight // eslint-disable-line no-void
        wrapper.style.height = `${target}px`

        const finish = () => {
            wrapper.removeEventListener('transitionend', onEnd)
            clearTimeout(timerRef.current)
            wrapper.style.height = ''
            setAnimating(false)
            if (!expanded) setClamped(true)
        }
        const onEnd = (event) => {
            if (event.target === wrapper && event.propertyName === 'height') finish()
        }
        wrapper.addEventListener('transitionend', onEnd)
        // transitionend does not fire when the transition is disabled or
        // interrupted; the timer guarantees the state settles regardless.
        timerRef.current = setTimeout(finish, DURATION_MS + 80)

        return () => {
            wrapper.removeEventListener('transitionend', onEnd)
            clearTimeout(timerRef.current)
        }
    }, [animating, expanded])

    const toggle = useCallback(() => {
        const wrapper = wrapperRef.current
        const content = contentRef.current
        const next = !expanded

        if (!wrapper || !content || prefersReducedMotion()) {
            setExpanded(next)
            setClamped(!next)
            return
        }

        if (!next) {
            // Measure what the clamped height will be before anything changes.
            content.classList.add('rmt-clamped')
            targetHeightRef.current = content.clientHeight
            content.classList.remove('rmt-clamped')
        }

        // Pin the current height so removing the clamp does not jump, then animate.
        wrapper.style.height = `${wrapper.offsetHeight}px`
        setClamped(false)
        setExpanded(next)
        setAnimating(true)
    }, [expanded])

    const onKeyDown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        toggle()
    }

    const showToggle = overflows || expanded || animating

    return (
        <div className={`rmt ${className}`.trim()}>
            <div ref={wrapperRef} className={`rmt-wrap ${animating ? 'is-animating' : ''}`}>
                <div ref={contentRef} className={`rmt-content ${clamped ? 'rmt-clamped' : ''}`} style={{ '--rmt-lines': lines }}>
                    {message}
                </div>
            </div>
            {showToggle && (
                <span className="my-anchor-css-class rmt-toggle font-bold" role="button" tabIndex={0} aria-expanded={expanded} onClick={toggle} onKeyDown={onKeyDown}>
                    {expanded ? t('showLess') : t('showMore')}
                </span>
            )}
        </div>
    )
}

export default ReadMoreText
